import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table, TableBody, TableCell, TableHead, TableRow
} from '@mui/material';
import { FaArrowLeft, FaChevronRight, FaChevronDown, FaEye, FaEyeSlash } from 'react-icons/fa';
import PositionSelector from '../../common/PositionSelector';
import ClusterSelector from '../../common/ClusterSelector';
import BusinessUnitSelector from '../../common/BusinessUnitSelector';
import { OrganizationalStatus, Grades, EmployeeReportingScope, OrgRoleFilter } from '../../common/filters';
import LoadingSpinner from '../../common/LoadingSpinner';
import { organizationApi } from '../../../services/api';
import { workforceCompositionApi } from '../../../services/api/workforceCompositionApi';
import { useAlert } from '../../../contexts/AlertContext';
import { ROLE_TYPES } from '../../../constants/roleTypes';
import { ORGANIZATIONAL_STATUSES } from '../../../constants/organizationalStatuses';
import { EMPLOYEE_REPORTING_SCOPE } from '../../../constants/employeeReportingScope';
import { POSITION_RETURN_TYPES, CLUSTER_RETURN_TYPES, BUSINESS_UNIT_RETURN_TYPES } from '../../../constants/selectorReturnTypes';
import { Container, Header, HeaderLeft, Title, BackButton } from '../styles';
import {
  SourceNote, ScrollArea, FilterBar, FilterBarClip, FilterBarInner, FilterRow, FilterGroup, FilterLabel,
  HeaderStats, HeaderStat, HeaderButton, ResultsCard, ResultsToolbar,
  ToolbarLabel, GroupToggle, GroupToggleButton, ResultsPlaceholder,
  TableWrapper, TreeRow, IndentedName, CountCell, EmptyNote
} from './styles';

/**
 * ADR-029 — Workforce Composition Report (Phase 2).
 *
 * Answers "how many individuals are <position> with <technical role>",
 * grouped by Cluster / Business Unit / project (delivery-path
 * attribution: everything derives from the single active FT allocation).
 * The API response also carries the D7 coverage block (position/role data
 * completeness) — not rendered here by product decision, but available if
 * a consumer wants it.
 */

const GROUP_DIMENSIONS = [
  { key: 'deliveryCenter', label: 'Cluster' },
  { key: 'serviceUnit', label: 'Business Unit' },
  { key: 'project', label: 'Project' },
  { key: 'position', label: 'Position' },
  { key: 'grade', label: 'Grade' },
  { key: 'functionalRole', label: 'Functional Role' },
  { key: 'technicalRole', label: 'Technical Role' },
  { key: 'organizationalRole', label: 'Organizational Role' }
];

const AXES = [
  { key: 'functional', type: ROLE_TYPES.FUNCTIONAL, label: 'Functional roles', stateKey: 'functionalRoleIds' },
  { key: 'technical', type: ROLE_TYPES.TECHNICAL, label: 'Technical roles', stateKey: 'technicalRoleIds' },
  { key: 'organizational', type: ROLE_TYPES.ORGANIZATIONAL, label: 'Organizational roles', stateKey: 'organizationalRoleIds' }
];

const toggleIn = (list, value) =>
  list.includes(value) ? list.filter(v => v !== value) : [...list, value];

const WorkforceCompositionReport = () => {
  const navigate = useNavigate();
  const { showAlert } = useAlert();

  // catalog (one call feeds positions narrowing + all three role axes — D9)
  const [structure, setStructure] = useState(null);

  // filters — this screen owns its own bar; only components are shared (D9)
  const [selectedPositions, setSelectedPositions] = useState([]);
  const [selectedGrades, setSelectedGrades] = useState([]);
  // D8: headcount default is EMPLOYED only (differs from the repository)
  const [selectedStatuses, setSelectedStatuses] = useState([ORGANIZATIONAL_STATUSES.EMPLOYED]);
  const [reportingScope, setReportingScope] = useState(EMPLOYEE_REPORTING_SCOPE.ALL);
  const [selectedDeliveryCenters, setSelectedDeliveryCenters] = useState([]);
  const [selectedServiceUnits, setSelectedServiceUnits] = useState([]);
  const [roleIdsByAxis, setRoleIdsByAxis] = useState({
    functionalRoleIds: [], technicalRoleIds: [], organizationalRoleIds: []
  });
  const [groupBy, setGroupBy] = useState(['deliveryCenter']);
  const [showFilters, setShowFilters] = useState(true);

  // data
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(new Set());

  useEffect(() => {
    organizationApi.getStructure()
      .then(setStructure)
      .catch(error => showAlert('Failed to load the role catalog', 'error', error));
  }, [showAlert]);

  const filterState = useMemo(() => ({
    positionIds: selectedPositions,
    gradeIds: selectedGrades,
    organizationalStatus: selectedStatuses,
    reportingScope,
    deliveryCenterIds: selectedDeliveryCenters,
    serviceUnitIds: selectedServiceUnits,
    ...roleIdsByAxis
  }), [selectedPositions, selectedGrades, selectedStatuses, reportingScope,
    selectedDeliveryCenters, selectedServiceUnits, roleIdsByAxis]);

  const fetchReport = useCallback(async () => {
    try {
      setLoading(true);
      const data = await workforceCompositionApi.getWorkforceComposition(filterState, { groupBy });
      setReport(data);
      // expand roots by default — keys are kind-prefixed full paths,
      // matching renderTreeRows
      const topKind = groupBy.includes('deliveryCenter') ? 'dc' : 'su';
      setExpanded(new Set((data.groups || []).map(g => `/${topKind}-${g.id ?? 'not-set'}`)));
    } catch (error) {
      showAlert(error.message || 'Failed to load the report', 'error', error);
    } finally {
      setLoading(false);
    }
  }, [filterState, groupBy, showAlert]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  // narrow each role axis to the selected positions' attached roles (§2.7)
  const availableRoleIdsByType = useMemo(() => {
    if (!structure || selectedPositions.length === 0) return null;
    const union = new Set();
    for (const position of structure.positions || []) {
      if (!selectedPositions.includes(position.id)) continue;
      for (const role of position.roles || []) union.add(role.id);
    }
    return union;
  }, [structure, selectedPositions]);

  // Prune role selections that fall outside the narrowed set when the
  // position filter changes — a stale selected role would silently keep
  // constraining the query (guaranteed empty result) with its chip dimmed
  // and unclickable. Mirrors BusinessUnitSelector's auto-clear on cluster
  // change.
  useEffect(() => {
    if (availableRoleIdsByType === null) return;
    setRoleIdsByAxis(prev => {
      let changed = false;
      const next = {};
      for (const [key, ids] of Object.entries(prev)) {
        const kept = ids.filter(id => availableRoleIdsByType.has(id));
        if (kept.length !== ids.length) changed = true;
        next[key] = kept;
      }
      return changed ? next : prev;
    });
  }, [availableRoleIdsByType]);

  const handleGroupByToggle = (key) => {
    setGroupBy(prev => {
      const next = toggleIn(prev, key);
      if (next.length > 3) {
        showAlert('Group by supports at most 3 dimensions', 'warning');
        return prev;
      }
      return next.length === 0 ? prev : next;
    });
  };

  const isTreeReport = groupBy.includes('deliveryCenter') || groupBy.includes('serviceUnit');
  const flatDims = groupBy.filter(d => !['deliveryCenter', 'serviceUnit'].includes(d));
  const dimLabel = dim => GROUP_DIMENSIONS.find(d => d.key === dim)?.label || dim;

  // In tree mode the first column carries EVERY grouping dimension, not just
  // the tree axes: cluster/BU nest as rows, and the remaining dimensions collapse
  // into one breakdown row per node. The header mirrors that exactly — ' / '
  // between nesting levels, ' · ' inside the breakdown leaf, the same
  // separator the breakdown row uses to join its own values. cluster/BU are taken
  // in canonical order (cluster outer, BU inner) because that is how the forest
  // nests, regardless of the order they were picked in.
  const treeColumnLabel = [
    ['deliveryCenter', 'serviceUnit'].filter(d => groupBy.includes(d)).map(dimLabel).join(' / '),
    flatDims.map(dimLabel).join(' · ')
  ].filter(Boolean).join(' / ');

  // Indentation comes from RENDER recursion depth, never from the server's
  // node.depth — server depths are local to each forest (the BU forest
  // attached under a cluster restarts at 0), so using them directly outdents BU
  // rows to the top level. `kind` distinguishes cluster from BU rows visually,
  // which matters because an BU often shares its cluster's name.
  const renderTreeRows = (nodes, parentKey = '', depth = 0, kind = 'dc') => nodes.flatMap((node) => {
    const key = `${parentKey}/${kind}-${node.id ?? 'not-set'}`;
    const hasSuSubtree = (node.serviceUnits || []).length > 0;
    const expandable = (node.children || []).length > 0 || hasSuSubtree
      || (node.breakdown?.length > 0);
    const isOpen = expanded.has(key);
    const toggle = () => setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });

    const rows = [(
      <TreeRow key={key} $expandable={expandable} $notSet={node.id === null} onClick={expandable ? toggle : undefined}>
        <TableCell>
          <IndentedName $depth={depth}>
            {expandable && (isOpen ? <FaChevronDown size={10} /> : <FaChevronRight size={10} />)}
            {node.name}
            {/* ADR-119: the cluster's Organizational Structure Type, as the
                selectors render it — muted code, full name on hover. */}
            {kind === 'dc' && node.orgStructureType?.code && (
              <span
                title={node.orgStructureType.name}
                style={{ color: '#6B7280', fontFamily: 'monospace', fontSize: 11 }}
              >{node.orgStructureType.code}</span>
            )}
            {kind === 'su' && (
              <span style={{
                fontSize: 10, fontWeight: 600, color: '#0369a1',
                background: '#e0f2fe', borderRadius: 4, padding: '1px 5px'
              }}>BU</span>
            )}
          </IndentedName>
        </TableCell>
        <CountCell>{node.directCount}</CountCell>
        <CountCell><strong>{node.rollupCount}</strong></CountCell>
      </TreeRow>
    )];

    if (isOpen) {
      // same-kind children first (sub-clusters under a cluster, sub-BUs under an BU),
      // then this cluster's Service-Unit forest, one level deeper
      rows.push(...renderTreeRows(node.children || [], key, depth + 1, kind));
      rows.push(...renderTreeRows(node.serviceUnits || [], key, depth + 1, 'su'));
      for (const b of node.breakdown || []) {
        rows.push(
          <TreeRow key={`${key}/b-${flatDims.map(d => b[d]?.id).join('-')}`}>
            <TableCell>
              <IndentedName $depth={depth + 1}>
                {flatDims.map(d => b[d]?.label).join(' · ')}
              </IndentedName>
            </TableCell>
            <CountCell>{b.count}</CountCell>
            <CountCell />
          </TreeRow>
        );
      }
    }
    return rows;
  });

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <BackButton onClick={() => navigate('/reports')}>
            <FaArrowLeft /> Back to Reports
          </BackButton>
          <Title>Workforce Composition</Title>
        </HeaderLeft>
        <HeaderStats>
          <HeaderStat>
            <div className="top">
              <span className="label">Individuals</span>
              <span className="value">{loading ? '…' : (report?.totalEmployees ?? '—')}</span>
            </div>
            <span className="sub">distinct — group counts may overlap</span>
          </HeaderStat>
          <HeaderButton onClick={() => setShowFilters(!showFilters)}>
            {showFilters ? <FaEyeSlash /> : <FaEye />}
            {showFilters ? 'Hide Filters' : 'Show Filters'}
          </HeaderButton>
        </HeaderStats>
      </Header>
      <ScrollArea>
        <SourceNote>
          Cluster, Business Unit and project derive from each individual's active full-time
          allocation (where the work lands) — not from the employment record. Roles count only on
          the full-time allocation.
        </SourceNote>

        <FilterBar $show={showFilters}>
          <FilterBarClip>
            <FilterBarInner>
          {/* These three carry their own MUI label — no FilterLabel header,
              which would duplicate it (chip groups below have no built-in
              label, so those keep theirs). */}
          <FilterRow>
            <FilterGroup>
              <PositionSelector multiple value={selectedPositions} onChange={setSelectedPositions}
                label="Positions" returnValue={POSITION_RETURN_TYPES.ID} size="small" />
            </FilterGroup>
            <FilterGroup>
              <ClusterSelector multiple value={selectedDeliveryCenters} onChange={setSelectedDeliveryCenters}
                label="Clusters" returnValue={CLUSTER_RETURN_TYPES.ID} size="small" showStructureType />
            </FilterGroup>
            <FilterGroup>
              <BusinessUnitSelector multiple value={selectedServiceUnits} onChange={setSelectedServiceUnits}
                includeInactive // ADR-120: reports keep ended units, marked "Ended <date>"
                label="Business Units" returnValue={BUSINESS_UNIT_RETURN_TYPES.ID} size="small" showEmptyOption={false}
                showStructureType />
            </FilterGroup>
          </FilterRow>
          <FilterRow>
            <FilterGroup>
              <FilterLabel>Grades</FilterLabel>
              <Grades selectedGrades={selectedGrades}
                onGradeToggle={(id) => setSelectedGrades(prev => toggleIn(prev, id))} />
            </FilterGroup>
            <FilterGroup>
              <FilterLabel>Status</FilterLabel>
              <OrganizationalStatus selectedStatuses={selectedStatuses}
                onStatusToggle={(status) => setSelectedStatuses(prev => toggleIn(prev, status))} />
            </FilterGroup>
            <FilterGroup>
              <FilterLabel>Scope</FilterLabel>
              <EmployeeReportingScope selectedScope={reportingScope} onScopeChange={setReportingScope} />
            </FilterGroup>
          </FilterRow>
          {AXES.map(axis => (
            <FilterRow key={axis.key}>
              <FilterGroup style={{ minWidth: '100%' }}>
                <FilterLabel>{axis.label}</FilterLabel>
                <OrgRoleFilter
                  axis={axis.type}
                  roles={structure ? structure.roles : null}
                  availableRoleIds={availableRoleIdsByType ? [...availableRoleIdsByType] : null}
                  selectedRoleIds={roleIdsByAxis[axis.stateKey]}
                  onRoleToggle={(roleId) => setRoleIdsByAxis(prev => ({
                    ...prev, [axis.stateKey]: toggleIn(prev[axis.stateKey], roleId)
                  }))}
                />
              </FilterGroup>
            </FilterRow>
          ))}
            </FilterBarInner>
          </FilterBarClip>
        </FilterBar>

        {/* Grouping is a view control, so it lives in the results toolbar —
            right-aligned like the Certifications report's view toggle, and
            still reachable when the filter bar is collapsed. */}
        <ResultsCard>
          <ResultsToolbar>
            <ToolbarLabel>Group by (max 3)</ToolbarLabel>
            <GroupToggle>
              {GROUP_DIMENSIONS.map(dim => (
                <GroupToggleButton
                  key={dim.key}
                  $active={groupBy.includes(dim.key)}
                  onClick={() => handleGroupByToggle(dim.key)}
                >
                  {dim.label}
                </GroupToggleButton>
              ))}
            </GroupToggle>
          </ResultsToolbar>

          {loading && (
            <ResultsPlaceholder>
              <LoadingSpinner message="Loading workforce composition..." />
            </ResultsPlaceholder>
          )}

          {!loading && report && (
            (!report.groups || report.groups.length === 0) ? (
              <ResultsPlaceholder>
                <EmptyNote>No individuals match the current filters.</EmptyNote>
              </ResultsPlaceholder>
            ) : (
              <TableWrapper>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {isTreeReport ? (
                        <>
                          <TableCell>{treeColumnLabel}</TableCell>
                          <TableCell align="right">Direct</TableCell>
                          <TableCell align="right">Total (incl. sub-units)</TableCell>
                        </>
                      ) : (
                        <>
                          {groupBy.map(dim => (
                            <TableCell key={dim}>{dimLabel(dim)}</TableCell>
                          ))}
                          <TableCell align="right">Individuals</TableCell>
                        </>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {isTreeReport
                      ? renderTreeRows(report.groups, '', 0,
                          groupBy.includes('deliveryCenter') ? 'dc' : 'su')
                      : report.groups.map((group, i) => {
                        const isNotSet = groupBy.some(dim => group[dim]?.id === null);
                        return (
                          <TreeRow key={i} $notSet={isNotSet}>
                            {groupBy.map(dim => (
                              <TableCell key={dim}>{group[dim]?.label ?? '—'}</TableCell>
                            ))}
                            <CountCell>{group.count}</CountCell>
                          </TreeRow>
                        );
                      })}
                  </TableBody>
                </Table>
              </TableWrapper>
            )
          )}
        </ResultsCard>
      </ScrollArea>
    </Container>
  );
};

export default WorkforceCompositionReport;
